import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import HikvisionNvrDashboardSkeleton from '../components/dashboard/HikvisionNvrDashboardSkeleton'
import HikvisionLiveVideo from '../components/dashboard/HikvisionLiveVideo'
import DeviceAvailabilityAnalytics from '../components/dashboard/DeviceAvailabilityAnalytics'
import DeviceOfflineBanner from '../components/dashboard/DeviceOfflineBanner'
import DevicePageTop from '../components/dashboard/DevicePageTop'
import HikvisionDeviceParams from '../components/dashboard/HikvisionDeviceParams'
import { schoolSurface, StatusDot } from '../components/dashboard/schoolEquipmentChrome'
import { SegmentedControl } from '../components/ui/surface'
import {
  deleteDoor,
  fetchDoorOverview,
  fetchDeviceSnapshotBlob,
  fetchNvrChannels,
  updateDoor,
  checkDeviceReachability,
} from '../api/hikvision'
import { fetchAlerts } from '../api/dashboard'
import { fetchFirePanelStatus } from '../api/fire'
import FirePanelStatusIcon, { FIRE_PANEL_STATE } from '../components/FirePanelStatusIcon'
import { firePanelDetailText } from '../lib/firePanelCopy'
import { formatDuration, formatStampAlmaty, formatUptimeSeconds } from '../lib/formatDuration'
import { pickNvrDownSince } from '../lib/nvrDownSince'
import { useTranslation } from 'react-i18next'

const isAdminOrEngineer = (role) => {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : ''
  return r === 'admin' || r === 'engineer'
}

const formatBytes = (val) => {
  if (val === null || val === undefined) return '—'
  const n = Number(val)
  if (!Number.isFinite(n) || n < 0) return '—'
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ']
  let v = n
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i += 1
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

const ChannelStatusBits = ({ channel, compact = false }) => {
  const online = channel.online
  const recording = channel.recording
  const cls = compact ? 'text-[9px]' : 'text-[10px]'
  return (
    <div className={`flex shrink-0 items-center gap-2 font-semibold uppercase tracking-wide ${cls}`}>
      <span className={online === true ? 'text-positive' : online === false ? 'text-critical' : 'text-muted-500'}>
        {online === true ? 'связь' : online === false ? 'нет связи' : '—'}
      </span>
      <span className={recording === true ? 'text-brand-200' : recording === false ? 'text-warning' : 'text-muted-500'}>
        {recording === true ? 'запись' : recording === false ? 'не пишется' : '—'}
      </span>
    </div>
  )
}

const VIEW_MODES = [
  { id: 'grid', label: 'Плитки', icon: 'grid_view' },
  { id: 'list', label: 'Список', icon: 'view_list' },
]

const STREAM_PROFILES = [
  { id: 'sub', label: 'Sub' },
  { id: 'main', label: 'Main' },
]

const ChannelTile = ({ channel, selected, onSelect, deviceId }) => {
  const rootRef = useRef(null)
  const [visible, setVisible] = useState(false)
  const [posterUrl, setPosterUrl] = useState(null)
  const [posterFailed, setPosterFailed] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '120px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!visible || !deviceId || !channel?.channel) return undefined
    let cancelled = false
    let objectUrl = null

    fetchDeviceSnapshotBlob(deviceId, String(channel.channel), { profile: 'sub' })
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url)
          return
        }
        objectUrl = url
        setPosterUrl(url)
        setPosterFailed(false)
      })
      .catch(() => {
        if (!cancelled) setPosterFailed(true)
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [visible, deviceId, channel?.channel])

  return (
    <button
      ref={rootRef}
      type="button"
      onClick={() => onSelect(channel)}
      className={`group relative flex aspect-[5/4] flex-col overflow-hidden rounded-xl text-left transition ${
        selected ? 'ring-1 ring-brand-400/50' : 'hover:bg-white/[0.03]'
      }`}
    >
      <div className="absolute inset-0 bg-[#0d111c]">
        {posterUrl ? (
          <img src={posterUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            {posterFailed ? (
              <span className="material-icons text-2xl text-muted-600">videocam_off</span>
            ) : (
              <span className="material-icons animate-pulse text-2xl text-muted-500">videocam</span>
            )}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
      </div>
      <div className="relative mt-auto flex min-w-0 flex-col gap-1 p-2.5">
        <p
          className="line-clamp-2 text-xs font-medium leading-snug text-white"
          title={channel.name || `Канал ${channel.channel}`}
        >
          {channel.name || `Канал ${channel.channel}`}
        </p>
        <div className="flex items-center justify-between gap-1">
          <span className="font-mono text-[10px] tabular-nums text-muted-400">#{channel.channel}</span>
          <ChannelStatusBits channel={channel} compact />
        </div>
      </div>
    </button>
  )
}

const ChannelRow = ({ channel, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(channel)}
    className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${
      selected
        ? 'bg-brand-400/15 text-white'
        : `${schoolSurface.tile} text-muted-200 hover:bg-white/[0.05]`
    }`}
  >
    <div className="min-w-0">
      <p className="truncate font-medium">{channel.name || `Канал ${channel.channel}`}</p>
      <p className="mt-0.5 font-mono text-[11px] tabular-nums text-muted-500">канал {channel.channel}</p>
    </div>
    <ChannelStatusBits channel={channel} />
  </button>
)

const HikvisionNvrDashboard = ({ setIsAuthenticated, onLogout, userRole }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { ip, deviceId } = useParams()

  const [isSidebarVisible, setSidebarVisible] = useState(true)
  const [overview, setOverview] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [firePanel, setFirePanel] = useState(null)
  const [seriesRefreshKey, setSeriesRefreshKey] = useState(0)
  const [fireHelpOpen, setFireHelpOpen] = useState(false)
  const [fireOpen, setFireOpen] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [storageOpen, setStorageOpen] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [downAlerts, setDownAlerts] = useState([])

  const [channels, setChannels] = useState([])
  const [channelsLoading, setChannelsLoading] = useState(false)
  const [channelsError, setChannelsError] = useState(null)
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [viewMode, setViewMode] = useState('grid')
  const [channelQuery, setChannelQuery] = useState('')
  const [profile, setProfile] = useState('sub')

  const [editingField, setEditingField] = useState(null)
  const [draft, setDraft] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [isChecking, setIsChecking] = useState(false)
  const [checkResult, setCheckResult] = useState(null)

  const canManage = isAdminOrEngineer(userRole)

  const loadChannels = async () => {
    if (!deviceId) return
    setChannelsLoading(true)
    setChannelsError(null)
    try {
      const data = await fetchNvrChannels(deviceId)
      const list = Array.isArray(data?.channels) ? data.channels : []
      setChannels(list)
      setSelectedChannel((prev) => {
        if (prev && list.some((c) => String(c.channel) === String(prev.channel))) return prev
        return list[0] || null
      })
    } catch (err) {
      setChannelsError(err?.response?.data?.message || err?.message || 'Не удалось загрузить каналы')
    } finally {
      setChannelsLoading(false)
    }
  }

  const fetchData = async ({ silent = false } = {}) => {
    if (!deviceId) return
    if (silent) setRefreshing(true)
    else setIsLoading(true)
    setError(null)
    try {
      const [overviewData, firePanelData, alertsData] = await Promise.all([
        fetchDoorOverview(deviceId),
        fetchFirePanelStatus({ deviceId }).catch(() => null),
        fetchAlerts().catch(() => []),
      ])
      const type = overviewData?.device?.device_type
      if (type === 'panic') {
        navigate(`/schools/${encodeURIComponent(ip)}/panic/${deviceId}`, { replace: true })
        return
      }
      if (type === 'door' || !type) {
        navigate(`/schools/${encodeURIComponent(ip)}/doors/${deviceId}`, { replace: true })
        return
      }
      setOverview(overviewData)
      setFirePanel(firePanelData)
      setDownAlerts(Array.isArray(alertsData) ? alertsData : [])
      setEditingField(null)
      if (silent) setSeriesRefreshKey((key) => key + 1)
      const up = overviewData?.metrics?.up
      const live = Number(up) === 1
      if (live) {
        await loadChannels()
      } else {
        setChannels([])
        setChannelsError(null)
        setSelectedChannel(null)
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Ошибка загрузки')
    } finally {
      setIsLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [deviceId])

  const handleSave = async (body) => {
    setIsSaving(true)
    setSaveError(null)
    try {
      await updateDoor(deviceId, body)
      setEditingField(null)
      setDraft({})
      fetchData({ silent: true })
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || 'Ошибка сохранения')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Удалить видеорегистратор? Действие необратимо.')) return
    setIsDeleting(true)
    setDeleteError(null)
    try {
      await deleteDoor(deviceId)
      navigate(`/schools/${encodeURIComponent(ip)}`)
    } catch (err) {
      setDeleteError(err?.response?.data?.message || err?.message || 'Ошибка удаления')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCheckReachability = async () => {
    if (!device?.host) {
      setCheckResult({ reachable: false, message: t('schoolDashboard.hikCheckHostRequired') })
      return
    }
    setIsChecking(true)
    setCheckResult(null)
    setDeleteError(null)
    try {
      const data = await checkDeviceReachability({
        id: Number(deviceId),
        host: device.host,
        port: device.port,
        use_https: !!device.use_https,
        device_type: device.device_type || 'nvr',
      })
      setCheckResult(data)
    } catch (err) {
      setCheckResult({
        reachable: false,
        message: err?.response?.data?.message || err?.message || t('schoolDashboard.hikCheckFailed'),
      })
    } finally {
      setIsChecking(false)
    }
  }

  const startEdit = (field, value) => {
    setSaveError(null)
    setEditingField(field)
    setDraft(value)
  }

  const handleCancelEdit = () => {
    setEditingField(null)
    setDraft({})
    setSaveError(null)
  }

  const filteredChannels = useMemo(() => {
    const q = channelQuery.trim().toLowerCase()
    if (!q) return channels
    return channels.filter((ch) => {
      const name = String(ch.name || '').toLowerCase()
      const id = String(ch.channel || '')
      return name.includes(q) || id.includes(q)
    })
  }, [channels, channelQuery])

  const device = overview?.device
  const metrics = overview?.metrics
  const source = overview?.source
  const upValue = metrics?.up === null || metrics?.up === undefined ? null : Number(metrics.up)
  const isUp = upValue === 1
  const isDown = upValue === 0
  const address = device?.host && device?.port != null
    ? `${device.host}:${device.port}`
    : device?.target
  const downSince = pickNvrDownSince(downAlerts, {
    deviceId,
    target: address || device?.target,
  })

  const disks = Array.isArray(metrics?.disks) ? metrics.disks : []
  const disksFilled = disks.filter((disk) => {
    const capacity = Number(disk.capacity_bytes) || 0
    const free = Number(disk.free_bytes) || 0
    if (capacity <= 0) return false
    return ((capacity - free) / capacity) >= 0.9
  }).length
  const disksMax = disks.length
  const info = metrics?.device_info || {}
  const model = device?.model || info.model
  const firmware = device?.firmware || info.firmware
  const serial = device?.serial || info.serial
  const downDuration = formatDuration(downSince)
  const downStamp = formatStampAlmaty(downSince)
  const fireState = firePanel?.state || FIRE_PANEL_STATE.NOT_CONNECTED
  const prominentFire =
    fireState === FIRE_PANEL_STATE.ON_FIRE
    || (!isDown && fireState === FIRE_PANEL_STATE.FAULT)
  const fireDotClass =
    isDown || fireState === FIRE_PANEL_STATE.NOT_CONNECTED
      ? 'bg-muted-500'
      : fireState === FIRE_PANEL_STATE.OK
        ? 'bg-positive'
        : fireState === FIRE_PANEL_STATE.FAULT
          ? 'bg-warning'
          : 'bg-critical'
  const uptimeSeconds = isUp && metrics?.uptime_seconds != null && metrics.uptime_seconds !== ''
    ? Number(metrics.uptime_seconds)
    : null
  const uptimeShown = Number.isFinite(uptimeSeconds) ? Math.round(uptimeSeconds) : null
  const stats = [
    { label: t('nvr.channels'), value: isUp ? (metrics?.channels_configured ?? metrics?.channels_total ?? null) : null },
    {
      label: t('nvr.uptime'),
      value: uptimeShown == null ? null : formatUptimeSeconds(uptimeShown),
      tone: uptimeShown == null ? undefined : uptimeShown < 10 * 60 ? 'text-critical' : 'text-positive',
    },
    { label: t('nvr.recording'), value: isUp ? (metrics?.channels_recording ?? null) : null },
    {
      label: t('nvr.disksOk'),
      value: isUp && metrics?.disks_total != null ? `${metrics.disks_ok ?? 0}/${metrics.disks_total}` : null,
    },
  ]

  const chartEmptyMessage = useMemo(() => {
    if (source && source.live === false && source.prometheus === false) {
      return 'Exporter недоступен — невозможно опросить устройство.'
    }
    if (source && source.prometheus === false) {
      return 'Метрики собираются. Исторические данные появятся после нескольких циклов сбора.'
    }
    return 'Нет данных для отображения за выбранный период.'
  }, [source])

  const goToSchool = () => navigate(`/schools/${encodeURIComponent(ip)}`)
  const refresh = () => fetchData({ silent: true })

  const analyticsSection = (
              <DeviceAvailabilityAnalytics
                t={t}
                deviceId={deviceId}
                currentUp={upValue}
                downSince={downSince}
                isDown={isDown}
                refreshKey={seriesRefreshKey}
                modes={[
                  { id: 'availability', label: t('nvr.chartAvailability'), title: t('nvr.availability') },
                  { id: 'channels', label: t('nvr.chartChannels'), title: t('nvr.channelsOverTime') },
                ]}
                secondaryKind="channels"
                emptyMessage={chartEmptyMessage}
                showRebootHistory
              />
  )

  return (
    <div className="relative flex min-h-screen flex-col overflow-clip bg-surface-base text-muted-100">
      <div className="absolute inset-0 -z-10 bg-brand-radial" />
      <Header
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={() => setSidebarVisible((prev) => !prev)}
        onLogout={onLogout}
        userEmail={localStorage.getItem('userEmail') || ''}
        userRole={userRole}
        setIsAuthenticated={setIsAuthenticated}
      />
      <div className="flex flex-1">
        {isSidebarVisible && (
          <Sidebar
            isSidebarVisible={isSidebarVisible}
            onLogout={onLogout}
            userRole={userRole}
            onClose={() => setSidebarVisible(false)}
          />
        )}
        <main className="relative flex min-w-0 flex-1 flex-col gap-6 bg-surface-base/60 px-4 py-6 transition-all duration-300 ease-brand sm:px-6 lg:px-10">
          <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-brand-linear/20 blur-3xl" />
          <div className="pointer-events-none absolute inset-y-0 right-0 -z-10 w-64 bg-brand-radial blur-3xl" />

          {isLoading ? (
            <HikvisionNvrDashboardSkeleton />
          ) : error ? (
            <div className="rounded-3xl bg-critical/10 px-6 py-8 text-sm text-critical">
              {error}
            </div>
          ) : !device ? (
            <div className="rounded-3xl bg-surface-1 px-6 py-12 text-center text-sm text-muted-300">
              Устройство не найдено
            </div>
          ) : (
            <>
              <DevicePageTop
                crumbLabel={device.school || t('schoolDashboard.backToList')}
                onBack={goToSchool}
                kind={t('equipment.typeSingular.nvr')}
                title={device.name}
                isUp={isUp}
                isDown={isDown}
                downDuration={downDuration}
                address={address}
                model={model}
                firmware={firmware}
                showDetails={showDetails}
                onToggleDetails={() => setShowDetails((v) => !v)}
                onRefresh={refresh}
                refreshing={refreshing}
                t={t}
              />

              {showDetails && (
                <HikvisionDeviceParams
                  device={device}
                  metrics={metrics}
                  serial={serial}
                  canManage={canManage}
                  t={t}
                  editingField={editingField}
                  draft={draft}
                  setDraft={setDraft}
                  startEdit={startEdit}
                  onCancelEdit={handleCancelEdit}
                  onSave={handleSave}
                  isSaving={isSaving}
                  saveError={saveError}
                  onCheckReachability={handleCheckReachability}
                  isChecking={isChecking}
                  checkResult={checkResult}
                  onDelete={handleDelete}
                  isDeleting={isDeleting}
                  deleteError={deleteError}
                />
              )}

              {isDown && (
                <DeviceOfflineBanner
                  downStamp={downStamp}
                  noConnectionKey="device.noConnection"
                  noConnectionSinceKey="device.noConnectionSince"
                  hintKey="device.offlineHint"
                  onRetry={refresh}
                  t={t}
                />
              )}

              {prominentFire && (
              <section className={`rounded-3xl p-6 ${schoolSurface.section}`}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-5">
                    <FirePanelStatusIcon
                      state={firePanel?.state || FIRE_PANEL_STATE.NOT_CONNECTED}
                      size="xl"
                    />
                    <div>
                      <h2
                        className={`font-display text-lg ${
                          firePanel?.state === FIRE_PANEL_STATE.ON_FIRE
                          || firePanel?.state === FIRE_PANEL_STATE.UNAVAILABLE
                            ? 'text-critical'
                            : firePanel?.state === FIRE_PANEL_STATE.FAULT
                              ? 'text-warning'
                              : 'text-white'
                        }`}
                      >
                        {t('fire.panel.sectionTitle')}
                      </h2>
                      <p className="mt-1 text-sm text-muted-300">
                        {t(`fire.panel.state.${firePanel?.state || FIRE_PANEL_STATE.NOT_CONNECTED}`)}
                      </p>
                      <p className="mt-1 max-w-md text-sm text-muted-400">
                        {firePanelDetailText(firePanel, t)}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFireHelpOpen((open) => !open)}
                      className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs transition ${
                        fireHelpOpen
                          ? 'bg-white/[0.08] text-white'
                          : 'bg-white/[0.04] text-muted-200 hover:bg-white/[0.08] hover:text-white'
                      }`}
                      aria-expanded={fireHelpOpen}
                      title="Как смотреть пожарку на этом регистраторе"
                    >
                      <span className="material-icons text-sm">help_outline</span>
                      Справка
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/fire-incidents')}
                      className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs transition ${
                        firePanel?.state === FIRE_PANEL_STATE.ON_FIRE
                          ? 'alarm-nav-pulse bg-critical/20 text-white'
                          : 'bg-white/[0.04] text-muted-200 hover:bg-white/[0.08] hover:text-white'
                      }`}
                    >
                      <span
                        className={`material-icons text-sm ${
                          firePanel?.state === FIRE_PANEL_STATE.ON_FIRE ? 'text-critical' : ''
                        }`}
                      >
                        local_fire_department
                      </span>
                      {t('fire.title')}
                    </button>
                  </div>
                </div>
                {fireHelpOpen && (
                  <ol className="mt-4 grid gap-3 text-sm text-muted-200 md:grid-cols-3">
                    <li className={`rounded-2xl px-4 py-3 ${schoolSurface.card}`}>
                      <span className="block text-[11px] uppercase tracking-[0.08em] text-muted-500">Порт 1 · имя пожар</span>
                      Срабатывание. Открывает пожарный инцидент. Провод с выхода «Пожар» (ПЦН3).
                    </li>
                    <li className={`rounded-2xl px-4 py-3 ${schoolSurface.card}`}>
                      <span className="block text-[11px] uppercase tracking-[0.08em] text-muted-500">Порт 16 · имя неисправность</span>
                      Готовность. Неисправность (ПЦН4) — алерт, не инцидент. Гаснет сам, когда контакт в норме.
                    </li>
                    <li className={`rounded-2xl px-4 py-3 ${schoolSurface.card}`}>
                      <span className="block text-[11px] uppercase tracking-[0.08em] text-muted-500">Где смотреть</span>
                      Страница «Оборудование»: чип «Пожарная сигнализация» у школы. Пока порты не названы так — карточка серая, «Не настроено».
                    </li>
                  </ol>
                )}
              </section>
              )}

              <section className={`grid grid-cols-2 gap-2 ${prominentFire ? 'sm:grid-cols-4' : 'sm:grid-cols-5'}`}>
                {stats.map((s) => (
                  <div key={s.label} className="rounded-2xl bg-white/[0.03] px-4 py-3">
                    <p className="text-xs text-muted-500">{s.label}</p>
                    <p className={`mt-1 font-display text-2xl tabular-nums ${s.value == null ? 'text-muted-600' : s.tone || 'text-white'}`}>
                      {s.value ?? '—'}
                    </p>
                  </div>
                ))}
                {!prominentFire && (
                  <button
                    type="button"
                    onClick={() => setFireOpen((open) => !open)}
                    className="rounded-2xl bg-white/[0.03] px-4 py-3 text-left transition hover:bg-white/[0.06]"
                  >
                    <p className="text-xs text-muted-500">{t('nvr.fire')}</p>
                    <p className="mt-1 inline-flex items-center gap-2 text-sm text-muted-300">
                      <span className={`h-2 w-2 rounded-full ${fireDotClass}`} />
                      {isDown
                        ? t('common.unknown')
                        : t(`fire.panel.state.${fireState}`)}
                    </p>
                  </button>
                )}
              </section>
              {fireOpen && !prominentFire && (
                <p className="px-1 text-sm text-muted-400">{firePanelDetailText(firePanel, t)}</p>
              )}

              {isUp && disks.length > 0 && (
                <section className={`overflow-hidden rounded-3xl ${schoolSurface.section}`}>
                  <button
                    type="button"
                    onClick={() => setStorageOpen((prev) => !prev)}
                    aria-expanded={storageOpen}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition hover:bg-white/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset"
                  >
                    <div>
                      <h2 className="font-display text-lg text-white">{t('nvr.disks')}</h2>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-baseline gap-1.5 font-display text-xl tabular-nums">
                        <span className="text-critical">{disksFilled}</span>
                        <span className="text-muted-600">/</span>
                        <span className="text-white">{disksMax}</span>
                      </span>
                      <span
                        className={`material-icons text-muted-400 transition-transform ${
                          storageOpen ? 'rotate-180' : ''
                        }`}
                      >
                        expand_more
                      </span>
                    </div>
                  </button>
                  {storageOpen && (
                    <div className="px-6 pb-6">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {disks.map((disk) => {
                          const ok = Number(disk.status) === 0
                          const capacity = Number(disk.capacity_bytes) || 0
                          const free = Number(disk.free_bytes) || 0
                          const usedPct =
                            capacity > 0
                              ? Math.min(100, Math.round(((capacity - free) / capacity) * 100))
                              : null
                          return (
                            <div
                              key={disk.disk_id}
                              className={`rounded-2xl p-4 ${schoolSurface.card}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/[0.04] text-brand-100">
                                    <span className="material-icons text-base">storage</span>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-500">Диск</p>
                                    <p className="font-display text-base text-white">№ {disk.disk_id}</p>
                                  </div>
                                </div>
                                <StatusDot tone={ok ? 'ok' : 'critical'} label={ok ? 'ОК' : 'Сбой'} />
                              </div>
                              <div className="mt-3 space-y-1 text-xs text-muted-300">
                                <p>
                                  Свободно:{' '}
                                  <span className="text-muted-100">{formatBytes(disk.free_bytes)}</span>
                                </p>
                                <p>
                                  Ёмкость:{' '}
                                  <span className="text-muted-100">{formatBytes(disk.capacity_bytes)}</span>
                                </p>
                              </div>
                              {usedPct != null && (
                                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                                  <div
                                    className={`h-full rounded-full ${
                                      usedPct > 90
                                        ? 'bg-critical'
                                        : usedPct > 75
                                          ? 'bg-warning'
                                          : 'bg-brand-400'
                                    }`}
                                    style={{ width: `${usedPct}%` }}
                                  />
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {!isUp && analyticsSection}

              {isUp ? (
              <section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                <div className="xl:col-span-8">
                  <div className={`rounded-3xl p-6 ${schoolSurface.section}`}>
                    <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h2 className="font-display text-lg text-white">
                          {t('nvr.channels')}{' '}
                          <span className="text-muted-500">
                            {metrics?.channels_online ?? '—'}/
                            {metrics?.channels_total ?? (channels.length || '—')}
                          </span>
                        </h2>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <SegmentedControl options={VIEW_MODES} value={viewMode} onChange={setViewMode} />
                        <button
                          type="button"
                          onClick={loadChannels}
                          className="inline-flex items-center gap-1 rounded-xl bg-white/[0.04] px-3 py-2 text-xs font-medium text-muted-300 transition hover:bg-white/[0.08] hover:text-white"
                        >
                          <span className="material-icons text-sm">refresh</span>
                          Обновить
                        </button>
                      </div>
                    </header>

                    {channels.length > 8 && (
                    <div className="relative mb-4">
                      <span className="material-icons pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-500">
                        search
                      </span>
                      <input
                        type="search"
                        value={channelQuery}
                        onChange={(e) => setChannelQuery(e.target.value)}
                        placeholder="Поиск по имени или номеру канала…"
                        className="w-full rounded-xl bg-surface-2 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-muted-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                      />
                    </div>
                    )}

                    {channelsLoading && (
                      <div className="flex h-40 items-center justify-center gap-3 text-sm text-muted-300">
                        <span className="material-icons animate-spin text-brand-300">progress_activity</span>
                        Загрузка каналов…
                      </div>
                    )}
                    {channelsError && (
                      <div className="rounded-2xl bg-critical/10 px-4 py-3 text-sm text-critical">
                        {channelsError}
                      </div>
                    )}
                    {!channelsLoading && !channelsError && filteredChannels.length === 0 && (
                      <div className="flex h-40 items-center justify-center rounded-2xl bg-surface-2 text-sm text-muted-500">
                        Нет каналов
                      </div>
                    )}
                    {!channelsLoading && filteredChannels.length > 0 && viewMode === 'grid' && (
                      <div className="@container max-h-[min(calc(0.8*100cqi+0.45rem),88vh)] overflow-y-auto overscroll-contain pr-1">
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                          {filteredChannels.map((ch) => (
                            <ChannelTile
                              key={ch.channel}
                              channel={ch}
                              deviceId={device.id}
                              selected={
                                selectedChannel &&
                                String(selectedChannel.channel) === String(ch.channel)
                              }
                              onSelect={setSelectedChannel}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {!channelsLoading && filteredChannels.length > 0 && viewMode === 'list' && (
                      <div className="flex max-h-[min(52rem,88vh)] flex-col gap-2 overflow-y-auto pr-1">
                        {filteredChannels.map((ch) => (
                          <ChannelRow
                            key={ch.channel}
                            channel={ch}
                            selected={
                              selectedChannel &&
                              String(selectedChannel.channel) === String(ch.channel)
                            }
                            onSelect={setSelectedChannel}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="xl:col-span-4">
                  <div className={`rounded-3xl p-6 ${schoolSurface.section}`}>
                    <header className="mb-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="truncate font-display text-lg text-white">
                          {selectedChannel
                            ? selectedChannel.name || `Канал ${selectedChannel.channel}`
                            : t('nvr.video')}
                        </h2>
                      </div>
                      <SegmentedControl options={STREAM_PROFILES} value={profile} onChange={setProfile} />
                    </header>
                    <div className="aspect-video overflow-hidden rounded-2xl bg-surface-2">
                      {selectedChannel && device.video_enabled !== false ? (
                        <HikvisionLiveVideo
                          key={`${device.id}-${selectedChannel.channel}-${profile}`}
                          deviceId={device.id}
                          videoEnabled
                          defaultChannel={String(selectedChannel.channel)}
                          profile={profile}
                          nvrMode
                          className="h-full"
                        />
                      ) : (
                        <div className="flex h-full min-h-[16rem] items-center justify-center text-sm text-muted-500">
                          {device.video_enabled === false
                            ? 'Видео отключено'
                            : 'Выберите канал'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
              ) : (
              <section className="flex items-center gap-3 rounded-3xl bg-surface-1 px-6 py-5 text-sm text-muted-400">
                <span className="material-icons text-muted-600">videocam_off</span>
                {t('nvr.channelsUnavailable')}
              </section>
              )}

              {isUp && analyticsSection}
            </>
          )}
        </main>
      </div>
    </div>
  )
}

export default HikvisionNvrDashboard
